import { useRedirect } from '@/features/redirect';
import { useDebouncedValue } from '@mantine/hooks';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { navSearchProvider } from './providers/nav-search';
import { parseSearchQuery } from './query/match-query';
import {
  applyAliasesToTree,
  sortActionsByAlias,
  sortTreeByAlias,
} from './query/search-aliases';
import {
  filterActionTree,
  findActionByPath,
  findByFullPath,
  findPathByTo,
  flattenActionTree,
  isAttachSelected,
  prependParentPath,
} from './query/tree';
import { useSearchProviders } from './registry';
import type {
  AppSearchAction,
  AppSearchNavNode,
  AppSearchRuntime,
} from './types';

export const useAppSearchResults = (
  query: string,
  completeSearch: (action: AppSearchAction) => void,
  navigation: AppSearchNavNode[],
): {
  tree: AppSearchAction[];
  actions: Array<AppSearchAction & { depth: number }>;
  scopeKey?: string;
  isLoading: boolean;
} => {
  const providers = useSearchProviders();
  const navigate = useNavigate();
  const openDashboardRedirect = useRedirect('open-dashboard');
  const { t } = useTranslation(['header', 'navigation']);
  const parsed = useMemo(() => parseSearchQuery(query), [query]);
  const [debouncedFilter] = useDebouncedValue(parsed.filter.trim(), 250);

  const runtime = useMemo<AppSearchRuntime>(
    () => ({
      query,
      navigation,
      navigate,
      openDashboard: (name) => openDashboardRedirect({ name }),
      completeSearch,
      t: (key) => t(key, { ns: 'navigation' }),
    }),
    [completeSearch, navigation, navigate, openDashboardRedirect, query, t],
  );

  const remoteProviders = useMemo(
    () => providers.filter((provider) => provider.attachTo),
    [providers],
  );

  const searchableRoutes = useMemo(
    () =>
      new Set(
        remoteProviders
          .map((provider) => provider.attachTo)
          .filter((to): to is string => Boolean(to)),
      ),
    [remoteProviders],
  );

  const navTree = useMemo(() => {
    const markSearchable = (nodes: AppSearchAction[]): AppSearchAction[] =>
      nodes.map((node) => ({
        ...node,
        searchable: Boolean(node.to && searchableRoutes.has(node.to)),
        children: node.children ? markSearchable(node.children) : node.children,
      }));

    return applyAliasesToTree(
      markSearchable(navSearchProvider.getActions?.(runtime) ?? []),
    );
  }, [runtime, searchableRoutes]);

  const remoteResults = useQueries({
    queries: remoteProviders.map((provider) => {
      const attachPath = provider.attachTo
        ? findPathByTo(navTree, provider.attachTo)
        : null;
      const selected = isAttachSelected(attachPath, parsed.scope);
      const searchText = provider.mode === 'page' ? debouncedFilter : '';

      if (provider.cacheQuery) {
        return {
          queryKey: provider.cacheQuery.queryKey,
          queryFn: provider.cacheQuery.queryFn,
          enabled: selected,
          refetchOnMount: false,
          refetchOnWindowFocus: false,
          refetchOnReconnect: false,
          staleTime: Infinity,
          cacheTime: Infinity,
        };
      }

      return {
        queryKey: ['app-search', provider.id, provider.mode, searchText],
        queryFn: ({ signal }: { signal?: AbortSignal }) =>
          provider.search?.(searchText, runtime, signal) ?? Promise.resolve([]),
        enabled: selected,
        keepPreviousData: true,
        staleTime: provider.mode === 'client' ? 60_000 : 30_000,
      };
    }),
  });

  return useMemo(() => {
    if (parsed.scope.length === 0) {
      const tree = sortTreeByAlias(
        filterActionTree(navTree, parsed),
        parsed.filter,
      );

      return {
        tree,
        actions: sortActionsByAlias(flattenActionTree(tree), parsed.filter),
        isLoading: false,
      };
    }

    const scopedNav = findActionByPath(navTree, parsed.scope);
    let searchTree: AppSearchAction[] = [];

    remoteProviders.forEach((provider, index) => {
      const attachPath = provider.attachTo
        ? findPathByTo(navTree, provider.attachTo)
        : null;
      if (!isAttachSelected(attachPath, parsed.scope)) {
        return;
      }

      const result = remoteResults[index];
      const parent = attachPath ? findActionByPath(navTree, attachPath) : null;
      const rawItems = provider.mapCache
        ? result?.data
          ? provider.mapCache(result.data, runtime)
          : []
        : ((result?.data as AppSearchAction[] | undefined) ?? []);
      const withPath = parent ? prependParentPath(rawItems, parent) : rawItems;
      const items =
        provider.mode === 'page'
          ? withPath
          : filterActionTree(withPath, {
              scope: [],
              filter: parsed.filter,
              entered: false,
            });

      searchTree = [...searchTree, ...items];
    });

    let tree: AppSearchAction[] = [];

    if (searchTree.length > 0) {
      const scopedItem = findByFullPath(searchTree, parsed.scope);
      tree = sortTreeByAlias(
        filterActionTree(scopedItem?.children ?? searchTree, {
          scope: [],
          filter: parsed.filter,
          entered: false,
        }),
        parsed.filter,
      );
    } else {
      tree = sortTreeByAlias(
        filterActionTree(scopedNav?.children ?? [], {
          scope: [],
          filter: parsed.filter,
          entered: false,
        }),
        parsed.filter,
      );
    }

    return {
      tree,
      actions: sortActionsByAlias(flattenActionTree(tree), parsed.filter),
      scopeKey: parsed.scope.join('/'),
      isLoading: remoteResults.some((result) => result.isFetching),
    };
  }, [navTree, parsed, remoteProviders, remoteResults, runtime]);
};